import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Breadcrumbs, Typography } from '@mui/material'
import { useState, useContext, useEffect, useRef } from 'react'

import '../styles/account-management.css'
import Dialog from '../components/common/dialog'
import { requestAccessToken } from '../api/auth.api'
import { AuthContext } from '../contexts/auth.context'
import { UserContext } from '../contexts/user.context'
import { checkTwoFactorAuth, login } from '../api/auth.api'
import { FormError, NewPassword, SystemUser } from '../types'
import { AppStateContext } from '../contexts/app-state.context'
import { handleGetIfPasswordIsValid } from '../utils/common.utils'
import NewPasswordForm from '../components/password/new-password-form'
import ProfileForm from '../components/account-management/profile-form'
import { NotificationsContext } from '../contexts/notifications.context'
import { updateSystemUserPassword, updateSystemUserData } from '../api/system-users.api'
import CurrentPasswordForm from '../components/account-management/current-password-form'
import { config } from '../config'
import { encryptForServer } from '../utils/crypto.utils'

const AccountManagement = () => {
  const navigate = useNavigate()
  const newPasswordFormRef = useRef()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateLoadingStatus } = useContext(AppStateContext)
  const { t } = useTranslation(['account-management', 'common'])
  const { userSession, setUserSessionData } = useContext(UserContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [errors, setErrors] = useState<FormError>({})
  const [formDisplayed, setFormDisplayed] = useState<string>('current')
  const [systemUserCurrentPassword, setSystemUserCurrentPassword] = useState<string>('')
  const [dialogChangePasswordIsLoading, setDialogChangePasswordIsLoading] = useState<boolean>(false)
  const [openChangeAccountPasswordModal, setOpenChangeAccountPasswordModal] = useState<boolean>(false)
  const [systemUserDataInitialState, setSystemUserDataInitialState] = useState<Partial<SystemUser>>({
    role: userSession?.role ?? '',
    username: userSession?.username ?? '',
    lastName: userSession?.lastName ?? '',
    firstName: userSession?.firstName ?? ''
  })
  const [systemUserData, setSystemUserData] = useState<Partial<SystemUser>>({
    role: userSession?.role ?? '',
    username: userSession?.username ?? '',
    lastName: userSession?.lastName ?? '',
    firstName: userSession?.firstName ?? ''
  })

  useEffect(() => {
    setSystemUserData({
      role: userSession?.role ?? '',
      username: userSession?.username ?? '',
      lastName: userSession?.lastName ?? '',
      firstName: userSession?.firstName ?? ''
    })
  }, [userSession])

  const handleValidateCurrentPasswordForm = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    if (systemUserCurrentPassword === '') {
      formDataIsValid = false
      formErrors.systemUserCurrentPassword = t('changeAccountPasswordModal.currentPasswordForm.errors.currentPassword')
    }

    if (!formDataIsValid || !userSession) {
      setErrors(formErrors)
      return false
    }

    try {
      setDialogChangePasswordIsLoading(true)
      const secret = config.secret
      const { cipherText, iv } = await encryptForServer(
        {
          password: systemUserCurrentPassword,
          username: userSession.username.trim()
        },
        String(secret)
      )

      // const loginResponse = await login(userSession.username, systemUserCurrentPassword)
      const loginResponse = await login(cipherText, iv)
      console.log('loginResponse', loginResponse)
      setFormDisplayed('new')
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ systemUserCurrentPassword: t('changeAccountPasswordModal.currentPasswordForm.errors.generalErrors.currentPasswordInvalid') })
      } else {
        setErrors({ systemUserCurrentPassword: t('changeAccountPasswordModal.currentPasswordForm.errors.generalErrors.serverError') })
      }

      setDialogChangePasswordIsLoading(false)
    } finally {
      setDialogChangePasswordIsLoading(false)
    }
  }

  const handleNewPasswordFormSubmit = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}
    const newPasswordFormData = newPasswordFormRef.current

    if (newPasswordFormData) {
      const typedNewPasswordFormData = newPasswordFormData as NewPassword
      const isValidPassword = handleGetIfPasswordIsValid(typedNewPasswordFormData.password)

      if (typedNewPasswordFormData.password === '') {
        formDataIsValid = false
        formErrors.password = t('changeAccountPasswordModal.newPasswordForm.errors.emptyNewPassword')
      }

      if (typedNewPasswordFormData.confirmPassword === '') {
        formDataIsValid = false
        formErrors.confirmPassword = t('changeAccountPasswordModal.newPasswordForm.errors.emptyConfirmNewPassword')
      }

      if (typedNewPasswordFormData.password !== '' && !isValidPassword) {
        formDataIsValid = false
        formErrors.password = t('changeAccountPasswordModal.newPasswordForm.errors.errorNewPassword')
      }

      if (isValidPassword && typedNewPasswordFormData.password !== '' && typedNewPasswordFormData.confirmPassword !== '' && typedNewPasswordFormData.password !== typedNewPasswordFormData.confirmPassword) {
        formDataIsValid = false
        formErrors.password = t('changeAccountPasswordModal.newPasswordForm.errors.mismatchPasswords')
        formErrors.confirmPassword = t('changeAccountPasswordModal.newPasswordForm.errors.mismatchPasswords')
      }

      if (!typedNewPasswordFormData.otpCode) {
        formDataIsValid = false
        formErrors.otpCodeError = true
        formErrors.newPasswordGeneralError = t('otpCodeForm.errors.emptyCode', { ns: 'two-factor-auth' })
      }

      if (typedNewPasswordFormData.otpCode && typedNewPasswordFormData.otpCode !== '' && typedNewPasswordFormData.otpCode.length < 6) {
        formDataIsValid = false
        formErrors.otpCodeError = true
        formErrors.newPasswordGeneralError = t('otpCodeForm.errors.incompleteCode', { ns: 'two-factor-auth' })
      }

      const isNumber = !isNaN(Number(typedNewPasswordFormData.otpCode))

      if (typedNewPasswordFormData.otpCode && typedNewPasswordFormData.otpCode !== '' && typedNewPasswordFormData.otpCode.length === 6 && !isNumber) {
        formDataIsValid = false
        formErrors.otpCodeError = true
        formErrors.newPasswordGeneralError = t('otpCodeForm.errors.errorCode', { ns: 'two-factor-auth' })
      }

      if (!formDataIsValid) {
        setErrors(formErrors)
        return
      }

      try {
        setDialogChangePasswordIsLoading(true)
        const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        await checkTwoFactorAuth(String(typedNewPasswordFormData.otpCode), accessTokenData.data.accessToken)
      } catch (error: any) {
        console.log(error)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
        } else if (error?.response?.status === 404) {
          setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        setDialogChangePasswordIsLoading(false)
        return
      }

      try {
        setDialogChangePasswordIsLoading(true)

        const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        const systemUserUpdatedData = await updateSystemUserPassword({ systemUserPassword: typedNewPasswordFormData.password }, accessTokenData.data.accessToken)

        if (systemUserUpdatedData.data.success === true) {
          setFormDisplayed('current')
          setSystemUserCurrentPassword('')
          setDialogChangePasswordIsLoading(false)
          setOpenChangeAccountPasswordModal(false)

          if (updateNewNotificationStatus) {
            updateNewNotificationStatus(true, 'password-updated')
          }
          return
        }

        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } catch (error: any) {
        console.log(error)
        if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        setDialogChangePasswordIsLoading(false)
      } finally {
        setDialogChangePasswordIsLoading(false)
      }
    }
  }

  const handleUpdateProfileData = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    if (systemUserData.firstName === '') {
      formDataIsValid = false
      formErrors.firstName = t('profileForm.errors.emptyFirstName')
    }

    if (systemUserData.lastName === '') {
      formDataIsValid = false
      formErrors.lastName = t('profileForm.errors.emptyLastName')
    }

    if (!formDataIsValid) {
      setErrors(formErrors)
      return
    }

    try {
      if (updateLoadingStatus) updateLoadingStatus(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const systemUserToUpdateId: string = userSession?._id as string

      await updateSystemUserData({ ...systemUserData }, systemUserToUpdateId, accessTokenData.data.accessToken)
      const userSessionUpdated = {
        ...userSession,
        ...systemUserData
      }

      setUserSessionData?.(userSessionUpdated)

      setSystemUserDataInitialState((currentSystemUserDataInitialState: Partial<SystemUser>) => {
        return {
          ...currentSystemUserDataInitialState,
          ...systemUserData
        }
      })

      if (updateNewNotificationStatus) {
        updateNewNotificationStatus(true, 'profile-updated')
      }

      if (updateLoadingStatus) updateLoadingStatus(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      if (updateLoadingStatus) updateLoadingStatus(false)
    } finally {
      if (updateLoadingStatus) updateLoadingStatus(false)
    }
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  return (
    <div className="section-container">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.firstLevel')}
        </Typography>
      </Breadcrumbs>
      <div className="account-management-section-header">{t('sectionTitle')}</div>
      <ProfileForm
        errors={errors}
        systemUserData={systemUserData}
        handleUpdateProfileData={handleUpdateProfileData}
        systemUserDataInitialState={systemUserDataInitialState}
        handleOpenChangePasswordModal={() => setOpenChangeAccountPasswordModal(true)}
        handleSetSystemUserData={(systemUserDataUpdated: Partial<SystemUser>) =>
          setSystemUserData((prevSystemUserData: Partial<SystemUser>) => ({
            ...prevSystemUserData,
            ...systemUserDataUpdated
          }))
        }
      />
      {openChangeAccountPasswordModal && (
        <Dialog
          errors={errors}
          dialogTitle={t('changeAccountPasswordModal.title')}
          actionButtonText={formDisplayed === 'current' ? t('changeAccountPasswordModal.currentPasswordForm.actionButtonText') : t('changeAccountPasswordModal.newPasswordForm.actionButtonText')}
          dialogIsLoading={dialogChangePasswordIsLoading}
          handleDialogAction={() => {
            if (formDisplayed === 'current') {
              handleValidateCurrentPasswordForm()
              return
            }

            handleNewPasswordFormSubmit()
          }}
          handleDialogOpenState={(openState: boolean) => {
            setOpenChangeAccountPasswordModal(openState)

            if (openState === false) {
              setErrors({})
              setFormDisplayed('current')
              setSystemUserCurrentPassword('')
              setDialogChangePasswordIsLoading(false)
            }
          }}
        >
          {formDisplayed === 'current' && (
            <CurrentPasswordForm
              errors={errors}
              systemUserCurrentPassword={systemUserCurrentPassword}
              handleSetSystemUserCurrentPassword={(currentPasswordToSet: string) => setSystemUserCurrentPassword(currentPasswordToSet)}
            />
          )}
          {formDisplayed === 'new' && (
            <NewPasswordForm
              showOtpModule
              errors={errors}
              ref={newPasswordFormRef}
              showActionsButton={false}
            />
          )}
        </Dialog>
      )}
    </div>
  )
}

export default AccountManagement
